import {assert} from 'Asserts';
import type {ProcedureType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {EXPERIMENT_VALIDATION_CONSTANTS} from 'studies/StudyConstants';
import type {
  ConditionGroup,
  ConditionValidationState,
} from 'studies/typed/shared/procedure-runs/design-state/useExperimentDesignState';
import {getProcedureRunConditionTextForProcedureType} from 'studies/TypedStudyUtils';

export interface ConditionDraft {
  numReplicates: number;
  name: string;
  description: string | undefined;
}

interface ConditionForValidation {
  name: string;
  description?: string;
}

export const DEFAULT_CONDITION_VALIDATION_STATE: ConditionValidationState = {
  isDuplicateName: false,
  isNameLengthOverLimit: false,
  isDescLengthOverLimit: false,
};

function isConditionNameLengthOverLimit(name: string): boolean {
  return name.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_NAME_MAX_LENGTH;
}

function isConditionDescriptionLengthOverLimit(description: string): boolean {
  return description.length > EXPERIMENT_VALIDATION_CONSTANTS.CONDITION_DESCRIPTION_MAX_LENGTH;
}

function getConditionValidationState(
  condition: ConditionForValidation,
  conditionNameCounts: Map<string, number>
): ConditionValidationState {
  const duplicateNameCount = conditionNameCounts.get(condition.name);
  assert(
    duplicateNameCount != null,
    `Duplicate name count should not be null for condition ${condition.name}`
  );
  return {
    isDuplicateName: duplicateNameCount > 1,
    isNameLengthOverLimit: isConditionNameLengthOverLimit(condition.name),
    isDescLengthOverLimit: isConditionDescriptionLengthOverLimit(condition.description ?? ''),
  };
}
/**
 * Computes a per-row validation state given the full set of (potentially renamed) names. Used
 * by both shape variants in replicateTableUtils; extracted so the duplicate-name detection only
 * enumerates the name set once per call.
 */
export function buildValidationStatesForUpdate(
  newConditions: ReadonlyArray<ConditionForValidation>
): Array<ConditionValidationState> {
  const conditionNameCounts = getConditionCountsByName(newConditions.map((c) => c.name));
  return newConditions.map((condition) => getConditionValidationState(condition, conditionNameCounts));
}

export function getDefaultConditionNames(length: number, procedureType: ProcedureType): Array<string> {
  return Array.from(
    {length},
    (_, conditionNumber: number) =>
      `${getProcedureRunConditionTextForProcedureType(procedureType, {capital: true})} ${conditionNumber + 1}`
  );
}

/**
 * Returns `numConditionsToGenerate` default condition names that are unique across `existingConditionNames`.
 *
 * Names follow the usual "Condition {x}" format and always pick the lowest available numeric suffix first.
 */
export function getNextDefaultConditionNames(
  existingConditionNames: Array<string>,
  numConditionsToGenerate: number,
  procedureType: ProcedureType
): Array<string> {
  if (numConditionsToGenerate <= 0) {
    return [];
  }
  const unavailableNames = new Set(existingConditionNames);
  const nextConditionNames: Array<string> = [];
  const conditionText = getProcedureRunConditionTextForProcedureType(procedureType, {capital: true});
  let conditionNumber = 1;

  while (nextConditionNames.length < numConditionsToGenerate) {
    const candidateName = `${conditionText} ${conditionNumber}`;
    if (!unavailableNames.has(candidateName)) {
      nextConditionNames.push(candidateName);
      unavailableNames.add(candidateName);
    }
    conditionNumber += 1;
  }
  return nextConditionNames;
}

export function getConditionCountsByName(names: Array<string>): Map<string, number> {
  const conditionCounts = new Map<string, number>();

  names.forEach((name) => {
    if (conditionCounts.has(name)) {
      // Increment count if name already exists
      conditionCounts.set(name, conditionCounts.get(name)! + 1);
    } else {
      // Initialize count if name does not exist
      conditionCounts.set(name, 1);
    }
  });

  return conditionCounts;
}

/**
 * Builds the initial PMDV coverage map for a newly created draft ConditionGroup.
 * When coverage is provided, seed those methods with null ids (covered but unsaved).
 * Otherwise return an empty map, preserving legacy "covers all methods" behavior.
 */
export function buildDraftConditionGroupPmdvMap(
  newConditionCoveredMethodIds?: ReadonlyArray<string>
): Record<string, string | null> {
  return newConditionCoveredMethodIds != null
    ? Object.fromEntries(newConditionCoveredMethodIds.map((methodId) => [methodId, null]))
    : {};
}

/**
 * Appends `numConditions` new draft {@link ConditionGroup} lanes to `conditions`, returning the next
 * conditions array and the matching `prevIndexes` (existing rows keep their index, new lanes are null).
 *
 * New lanes seed `conditionIdsByPmdvId` from `coveredMethodIds` (the null-id / empty-map draft convention
 * the save serializers consume to detect new lanes). Existing rows are re-emitted with refreshed
 * duplicate-name and length validation, since new names can flip those flags.
 *
 * Shared by the left-panel add-condition buttons and the flowchart add-replicate node so both produce
 * identical draft lanes.
 */
export function buildConditionsWithAddedGroups(
  conditions: Array<ConditionGroup>,
  {
    numConditions,
    numReplicates,
    procedureType,
    coveredMethodIds,
  }: {
    numConditions: number;
    numReplicates: number;
    procedureType: ProcedureType;
    coveredMethodIds?: ReadonlyArray<string>;
  },
  // Injectable so tests can force generated names that collide with existing ones and exercise the
  // re-validation path; production callers always use the default collision-avoiding generator.
  generateNextConditionNames: typeof getNextDefaultConditionNames = getNextDefaultConditionNames
): {conditions: Array<ConditionGroup>; prevIndexes: Array<number | null>} {
  const existingConditionNames = conditions.map((condition) => condition.name);
  const newConditionNames = generateNextConditionNames(existingConditionNames, numConditions, procedureType);
  const allConditionNames = existingConditionNames.concat(newConditionNames);
  const conditionNameCounts = getConditionCountsByName(allConditionNames);

  return {
    conditions: [
      // Re-emit existing conditions with refreshed validation state (duplicate-name flags may flip
      // when new conditions share names with existing ones).
      ...conditions.map((condition) => ({
        ...condition,
        validationState: getConditionValidationState(condition, conditionNameCounts),
      })),
      ...Array.from(
        {length: numConditions},
        (_, index: number): ConditionGroup => ({
          conditionIdsByPmdvId: buildDraftConditionGroupPmdvMap(coveredMethodIds),
          numReplicates,
          name: newConditionNames[index],
          validationState: getConditionValidationState(
            {name: newConditionNames[index], description: undefined},
            conditionNameCounts
          ),
        })
      ),
    ],
    prevIndexes: [...conditions.map((_, i) => i), ...new Array(numConditions).fill(null)],
  };
}
